import React, { Suspense, forwardRef } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { lazyWithChunkReload } from "@/utils/lazyWithChunkReload";
import type { HtmlEditorHandle, HtmlEditorProps } from "./types";

// CodeMirror, Emmet and Prettier stay out of the main bundle.
const CodeMirrorHtmlEditor = lazyWithChunkReload(() => import("./CodeMirrorHtmlEditor"));

/** Read-only stand-in in the same box while the editor chunk loads, so nothing typed is lost. */
const LoadingEditor: React.FC<HtmlEditorProps> = ({
  id,
  "aria-labelledby": labelledBy,
  value,
  singleLine,
  className,
}) => {
  const shared = { id, "aria-labelledby": labelledBy, "aria-busy": true, value, readOnly: true };
  return singleLine ? (
    <Input {...shared} className={cn("font-mono", className)} />
  ) : (
    <Textarea
      {...shared}
      spellCheck={false}
      className={cn("resize-none font-mono text-xs leading-relaxed", className)}
    />
  );
};

/** IDE-style HTML editor (CodeMirror), loaded on first render. */
const HtmlEditor = forwardRef<HtmlEditorHandle, HtmlEditorProps>(function HtmlEditor(props, ref) {
  return (
    <Suspense fallback={<LoadingEditor {...props} />}>
      <CodeMirrorHtmlEditor ref={ref} {...props} />
    </Suspense>
  );
});

export default HtmlEditor;
export type { HtmlEditorHandle, HtmlEditorProps, TemplateVariable } from "./types";
